<template>
  <teleport to="body">
    <div class="modal-overlay">
      <div class="modal-container modal-full-width">
        <div v-if="isLoading">
          <LoadingSpinner />
        </div>

        <!-- Ações do Modal no topo -->
        <div class="modal-footer modal-footer-top">
          <b-button class="btn btn-secondary me-2" @click="fecharModal">Cancelar</b-button>
          <b-button
            :variant="isGeneratingPdf ? 'success' : 'danger'"
            class="btn-pdf"
            :disabled="isGeneratingPdf"
            @click="gerarPDF"
          >
            <i class="bi bi-file-earmark-pdf"></i>
            <span v-if="isGeneratingPdf">Gerando…</span>
            <span v-else>Baixar</span>
          </b-button>
        </div>

        <div class="modal-header">
          <h6>Contrato de Matrícula</h6>
        </div>
        <div class="spacer"></div>
        <div ref="contratoContent">
          <!-- Cabeçalho PDF -->
          <div class="recibo-header">
            <img
              v-if="logoRenderSrc && !logoFailed"
              :src="logoRenderSrc"
              alt="Logomarca"
              class="header-logo"
              @error="onLogoError"
              @load="onLogoLoad"
            />
            <img
              v-else-if="logoFallback"
              :src="logoFallback"
              alt="Logomarca"
              class="header-logo"
              @load="onLogoLoad"
            />
            <div class="escola-info">
              <h4>{{ empresa.nome }}</h4>
              <p>{{ empresa.slogan }}</p>
              <p>
                {{ empresa.endereco }}, {{ empresa.numero }}
                <span
                  v-if="empresa.complemento && empresa.complemento !== 'null'"
                >
                  , {{ empresa.complemento }}
                </span>
                - {{ empresa.bairro }}
              </p>
              <p>
                {{ empresa.municipio }} - {{ empresa.estado }}, CEP: {{ empresa.cep }}
              </p>
              <p>
                <i class="bi bi-telephone"></i> {{ empresa.telefone }}
                <i class="bi bi-whatsapp text-success"></i>
                {{ empresa.whatsapp }}
              </p>
            </div>
          </div>
          <!-- Contrato nº e Vigência -->
          <div class="recibo-info text-center my-3">
            <h4>
              <strong>Contrato nº</strong> {{ dadosContrato.numero_contrato }}
            </h4>
            <h6>
              <strong>Vigência:</strong>
              {{ dadosContrato.inicio_vigencia }} a
              {{ dadosContrato.termino_vigencia }}
            </h6>
          </div>
          <!-- Tabela Contratante/Aluno -->
          <table class="table table-sm mt-4">
            <tbody>
              <tr>
                <td style="vertical-align: top; width:50%">
                  <strong>CONTRATANTE</strong><br />
                  Nome: {{ dadosContrato.responsavel_nome }}<br />
                  CPF: {{ formatarCPF(dadosContrato.responsavel_cpf) }}<br />
                  RG: {{ dadosContrato.responsavel_rg }}<br />
                  Sócio: {{ dadosContrato.responsavel_socio }}<br />
                  Matrícula: {{ dadosContrato.matricula }}<br />
                  Telefones:
                  {{ responsavelTelefonesFormatados }}<br />
                  E-mail: {{ dadosContrato.responsavel_email }}<br />
                  Endereço: {{ dadosContrato.responsavel_endereco }}<br />
                  Bairro: {{ dadosContrato.responsavel_bairro }}<br />
                  Cidade: {{ dadosContrato.responsavel_cidade }}<br />
                  UF: {{ dadosContrato.responsavel_uf}} - CEP:
                  {{ dadosContrato.responsavel_cep }}<br />
                </td>
                <td style="vertical-align: top; width:50%">
                  <strong>ALUNO</strong><br />
                  Nome: {{ dadosContrato.aluno_nome }}<br />
                  Data Nasc.: {{ dadosContrato.aluno_data_nascimento }}<br />
                  Idade: {{ idadeAluno }}<br />
                  <div class="mt-1">
                    <p></p>
                    <strong>PLANO CONTRATADO</strong><br />
                    Tipo de Plano: {{ tipoPlanoContrato }}<br />
                    Aulas por Semana: {{ aulasPorSemanaContrato }}<br />
                    Nível: {{ nivelContrato }}<br />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Corpo do Contrato -->
          <div class="contrato-corpo" v-if="contratoProcessado">
            <div class="ql-editor" v-safe-html="contratoProcessado"></div>
          </div>
          <div v-else-if="loadingTemplate">
            <p>Carregando contrato...</p>
          </div>
          <div v-else-if="erroTemplate">
            <p class="text-danger">Erro: {{ erroTemplate }}</p>
          </div>
        </div>
        <!-- Ações -->
        <div class="modal-footer">
          <b-button class="btn btn-secondary me-2" @click="fecharModal"
            >Cancelar</b-button
          >
          <b-button
            :variant="isGeneratingPdf ? 'success' : 'danger'"
            class="btn-pdf"
            :disabled="isGeneratingPdf"
            @click="gerarPDF"
          >
            <i class="bi bi-file-earmark-pdf"></i>
            <span v-if="isGeneratingPdf">Gerando…</span>
            <span v-else>Baixar</span>
          </b-button>
        </div>
      </div>
    </div>
  </teleport>
</template>


  <script>
import { renderHtmlTemplate } from '@/utils/safeHtml';
  import axios from "@/services/axios";
  import { BButton } from "bootstrap-vue-next";
  import pdfMake from "pdfmake/build/pdfmake";
  import pdfFonts from "pdfmake/build/vfs_fonts";
  import htmlToPdfmake from "html-to-pdfmake";
  import globalUtils from '@/utils/globalUtils.js';
  import { PDFDocument } from 'pdf-lib';

  // Corrige import: usa pdfFonts.vfs
  pdfMake.vfs = pdfFonts.vfs;

  export default {
    name: "ImprimirMatricula",
    inheritAttrs: false,       // ← evita warning de atributos extras (turma, dias_semana, etc)
    emits: ['fechar'],         // ← declara que seu componente emite “fechar”
    components: { BButton },
    props: {
        dadosContrato: { type: Object, required: true },
        turma: { type: String, default: '' },
        dias_semana: { type: String, default: '' },
        horario:        { type: String, default: '' } // ← agora fecha o objeto e define valor padrão
    },
    data() {
      return {
        contratoTemplate: "",
        contratoProcessado: "",
        loadingTemplate: false,
        erroTemplate: "",
        empresa: {},
        isLoading: true,
        isGeneratingPdf: false,
        logoFailed: false,
        logoFallback: '',

        // ✅ Anexo do contrato (PDF) — será incluído no final
        contratoAnexoCaminho: '',
        contratoAnexoNome: ''
      };
    },
    computed: {
      idadeAluno() {
        if (!this.dadosContrato.aluno_data_nascimento) return '';
        const [dia, mes, ano] = this.dadosContrato.aluno_data_nascimento.split('/');
        const nasc = new Date(`${ano}-${mes}-${dia}`);
        const hoje = new Date();
        let idade = hoje.getFullYear() - nasc.getFullYear();
        const m = hoje.getMonth() - nasc.getMonth();
        if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
        return idade;
      },

      // ===== Plano Contratado (com fallbacks) =====
      tipoPlanoContrato() {
        const d = this.dadosContrato || {};
        return (
          d.tipo_plano ||
          d.plano_tipo ||
          d.plano_nome ||
          d.tipoPlano ||
          d.plano ||
          ''
        );
      },
      aulasPorSemanaContrato() {
        const d = this.dadosContrato || {};
        return (
          d.aulas_semana ??
          d.aulasSemana ??
          d.aulas_por_semana ??
          d.aulasPorSemana ??
          d.qtd_aulas_semana ??
          d.quantidade_aulas_semana ??
          ''
        );
      },
      nivelContrato() {
        const d = this.dadosContrato || {};
        return (
          d.nivel ||
          d.nivel_nome ||
          d.nome_nivel ||
          d.nivel_turma ||
          ''
        );
      },

      responsavelTelefoneFormatado() {
        return this.formatarTelefoneComDdi(
          this.dadosContrato?.responsavel_telefone,
          this.dadosContrato?.responsavel_telefone_ddi
        );
      },

      responsavelCelularFormatado() {
        return this.formatarTelefoneComDdi(
          this.dadosContrato?.responsavel_celular,
          this.dadosContrato?.responsavel_celular_ddi
        );
      },

      responsavelTelefonesFormatados() {
        const tels = [
          this.responsavelTelefoneFormatado,
          this.responsavelCelularFormatado
        ].filter(Boolean);

        return tels.length ? tels.join(' / ') : 'Não informado';
      },

      logoRenderSrc() {
        const raw = (this.$globalState?.logoPath || '').trim();
        if (!raw) return '';
        try {
          // Garante URL absoluta correta em localhost e em xxxx.local
          return new URL(raw, window.location.origin).href;
        } catch {
          return raw; // último recurso
        }
      }
    },
    methods: {
      formatarTelefoneComDdi(numero, ddi = '') {
        const n = String(numero || '').trim();
        if (!n) return '';
        if (/^\+\d/.test(n)) return n;

        const d = String(ddi || '').trim();
        if (!d) return n;

        return `${d} ${n}`.trim();
      },

      onLogoError() {
        // Tenta novamente sem query (?v=...) para evitar 404 por cache/rotas
        try {
          const u = new URL(this.logoRenderSrc);
          u.search = '';
          this.logoFallback = u.href;
        } catch {
          this.logoFallback = '';
        }
        this.logoFailed = true;
      },
      onLogoLoad() {
        this.logoFailed = false;
      },
      async carregarContratoTemplate() {
        this.loadingTemplate = true;
        this.erroTemplate = "";
        try {
          const response = await axios.get(
            `/configuracoes/documentos`
          );
          const documentos = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data.data)
              ? response.data.data
              : [];
          const contrato = documentos.find(item =>
            item.titulo === "Contrato de Matrícula"
          );
          if (!contrato) throw new Error("Modelo não encontrado.");
          this.contratoTemplate = contrato.conteudo;

          // ✅ pega anexo (se existir) — regra: sempre vai no final
          this.contratoAnexoCaminho = contrato.anexo_caminho || '';
          this.contratoAnexoNome    = contrato.anexo_arquivo_nome || '';

          this.processarTemplate();

        } catch (error) {
          console.error("Erro ao carregar contrato:", error);
          this.erroTemplate = error.message || "Erro ao carregar modelo";
        } finally {
          this.loadingTemplate = false;
        }
      },
      async carregarEmpresa() {
        try {
          const response = await axios.get(
            `/configuracoes/empresa`
          );
          const data = response.data;
          this.empresa = {
            nome: data.nome || data.razao_social,
            cnpj: globalUtils.formatarCNPJ(data.cnpj) || '',
            slogan: data.slogan || '',
            endereco: data.endereco || '',
            numero: data.numero || '',
            complemento: data.complemento || '',
            bairro: data.bairro || '',
            municipio: data.municipio_nome || '',
            estado: data.estado_sigla || '',
            cep: data.cep || '',
            telefone: data.telefone1 || '',
            whatsapp: data.whatsapp_url || ''
          };
        } catch (error) {
          console.error("Erro ao carregar empresa:", error);
        }
      },
      formatarCPF(cpf) {
        if (!cpf) return '';
        cpf = cpf.replace(/\D/g, '');
        if (cpf.length !== 11) return cpf;
        return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      },
    processarTemplate() {
      const d = this.dadosContrato;
      const e = this.empresa;   // pega o objeto empresa

      const substit = {
        contrato_numero: d.numero_contrato,
        inicio_vigencia: d.inicio_vigencia,
        termino_vigencia: d.termino_vigencia,
        data_hoje_extenso: d.data_hoje_extenso,
        responsavel_nome: d.responsavel_nome,
        responsavel_rg: d.responsavel_rg,
        responsavel_cpf: this.formatarCPF(d.responsavel_cpf),
        responsavel_email: d.responsavel_email,
        responsavel_telefone: this.responsavelTelefoneFormatado,
        responsavel_celular: this.responsavelCelularFormatado,
        responsavel_endereco: d.responsavel_endereco,
        responsavel_bairro: d.responsavel_bairro,
        responsavel_cidade: d.responsavel_cidade,
        responsavel_uf: d.responsavel_uf,
        responsavel_cep: d.responsavel_cep,
        responsavel_socio: d.responsavel_socio,
        aluno_nome: d.aluno_nome,
        aluno_data_nascimento: d.aluno_data_nascimento,
        idade_aluno: this.idadeAluno,
        tipo_plano: this.tipoPlanoContrato,
        aulas_por_semana: this.aulasPorSemanaContrato,
        nivel: this.nivelContrato,
        turma: this.turma,
        dias_semana: this.dias_semana,
        horario: this.horario,
        matricula: d.matricula || '',
        tabela_mensalidade: d.tabela_mensalidade || '',
        matricula_valor_com_desconto: d.matricula_valor_com_desconto || '',
        nome_escola:        e.nome,
        endereco_escola:    e.endereco,
        numero_escola:      e.numero,
        bairro_escola:      e.bairro,
        complemento_escola: e.complemento,
        cep_escola:         e.cep,
        municipio_escola:   e.municipio,
        estado_escola:      e.estado,
        cnpj_escola:        e.cnpj, 

      };
      this.contratoProcessado = renderHtmlTemplate(this.contratoTemplate, substit, ['tabela_mensalidade']);
    },

    // ==========================
    // Helpers: gerar blob pdfMake
    // ==========================
    _pdfMakeToBlob(docDefinition) {
      return new Promise((resolve, reject) => {
        try {
          pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob));
        } catch (e) {
          reject(e);
        }
      });
    },

    // ==========================
    // Helpers: baixar Blob
    // ==========================
    _downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    // ==========================
    // Merge: contrato + anexo (PDF)
    // ==========================
    async _mergePdfWithAnexo(contractBlob, anexoUrlAbs) {
      // contract
      const contractBytes = await contractBlob.arrayBuffer();
      const contractPdf = await PDFDocument.load(contractBytes);

      // anexo
      const anexoRes = await fetch(anexoUrlAbs, { cache: 'no-store' });
      if (!anexoRes.ok) {
        throw new Error(`Falha ao baixar anexo (HTTP ${anexoRes.status})`);
      }
      const anexoBytes = await anexoRes.arrayBuffer();
      const anexoPdf = await PDFDocument.load(anexoBytes);

      const anexoPages = await contractPdf.copyPages(anexoPdf, anexoPdf.getPageIndices());
      anexoPages.forEach((p) => contractPdf.addPage(p));

      const mergedBytes = await contractPdf.save();
      return new Blob([mergedBytes], { type: 'application/pdf' });
    },

    _absUploadUrl(caminho) {
      if (!caminho) return '';
      const apiUrl = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      const rel = String(caminho).startsWith('/') ? caminho : `/${caminho}`;
      // Se já for URL completa, retorna como está
      if (/^https?:\/\//i.test(rel)) return rel;
      return `${apiUrl}${rel}`;
    },



    async gerarPDF() {
      if (this.isGeneratingPdf) return;
      this.isGeneratingPdf = true;

      try {
        // 1) HTML bruto do Quill
        const htmlRaw = this.$refs.contratoContent
          .querySelector('.ql-editor')
          .innerHTML;

        const htmlJustified = htmlRaw.replace(
          /class="ql-align-justify"/g,
          'style="text-align: justify;"'
        );

          
        // 2) Converte e ajusta cada nó
        const rawContent = htmlToPdfmake(htmlJustified, { tableAutoSize: true });
        const pdfContent = rawContent.map(node => {
          // Texto simples ou array de linhas
          if (node.text || node.stack) {
            return {
              ...node,
              alignment: node.alignment || 'justify',
              fontSize: 9,          // força 9pt
              margin: [0, 0, 0, 0]  // espaço mínimo entre parágrafos
            };
          }
          // Outros (tabelas, imagens) ficam iguais
          return node;
        });

        // 3) Pré-carrega a logo (agora tolerante a erro)
        const logoDataUrl = await this._getLogoDataUrl(); // pode ser null e tudo bem

        // 4) Monta o PDF
        const docDefinition = {
          pageSize: 'A4',
          pageMargins: [40, 120, 40, 60],

          header: (currentPage, pageCount, pageSize) => {
            const rightStack = {
              stack: [
                { text: this.empresa.nome,   bold: true, fontSize: 14, alignment: 'right' },
                { text: this.empresa.slogan, fontSize: 8,  alignment: 'right' },
                { text: `${this.empresa.endereco}, ${this.empresa.numero}`, fontSize: 7, alignment: 'right' },
                { text: `${this.empresa.bairro}${ this.empresa.complemento ? ' – ' + this.empresa.complemento : ''}`, fontSize: 7, alignment: 'right' },
                { text: `${this.empresa.municipio} - ${this.empresa.estado}, CEP: ${this.empresa.cep}`, fontSize: 7, alignment: 'right' },
                { text: `Tel: ${this.empresa.telefone} | WhatsApp: ${this.empresa.whatsapp}`, fontSize: 7, alignment: 'right' }
              ]
            };

            // ✅ Coluna da esquerda com largura fixa (não deixa a logo “esticar” o layout)
            const leftLogo = logoDataUrl
              ? {
                  stack: [
                    {
                      image: logoDataUrl,
                      fit: [120, 65],          // ✅ tamanho máximo (ajuste aqui)
                      alignment: 'left',
                      margin: [0, 0, 0, 0]
                    }
                  ],
                  width: 120                 // ✅ largura fixa da coluna da logo
                }
              : { text: '', width: 120 };

            return {
              columns: [
                leftLogo,
                { ...rightStack, width: '*' }
              ],
              columnGap: 10,
              margin: [40, 30, 40, 0],
              canvas: [
                { type: 'line', x1: 40, y1: 55, x2: pageSize.width - 40, y2: 55, lineWidth: 1 }
              ]
            };
          },

          footer: (currentPage, pageCount) => ({
            columns: [
              { text: `Contrato nº ${this.dadosContrato.numero_contrato} - Impresso por ${this.$globalState.userName}`, 
                alignment: 'left', 
                margin: [40, 0],
                color: 'gray',     // cor cinza
                italics: true      // itálico
              },

              { text: `Página ${currentPage} de ${pageCount}`, 
                alignment: 'right', 
                margin: [0, 0, 40],
                color: 'gray',     // cor cinza
                italics: true      // itálico
              }
            ],
            fontSize: 8
          }),

          content: [
            {
              text: `Contrato nº ${this.dadosContrato.numero_contrato}`,
              fontSize: 14,
              bold: true,
              alignment: 'center',
              margin: [0, 10, 0, 2]
            },
            {
              text: `Vigência: ${this.dadosContrato.inicio_vigencia} a ${this.dadosContrato.termino_vigencia}`,
              fontSize: 10,
              alignment: 'center',
              margin: [0, 0, 0, 10]
            },
            {
              table: {
                widths: ['40%', '60%'],
                body: [
                  [
                    {
                      stack: [
                        { text: 'CONTRATANTE', bold: true, fontSize: 9 },
                        `Nome: ${this.dadosContrato.responsavel_nome}`,
                        `CPF: ${this.formatarCPF(this.dadosContrato.responsavel_cpf)}`,
                        `Sócio: ${this.dadosContrato.responsavel_socio}`,
                        `Matrícula: ${this.dadosContrato.matricula}`,
                        `Telefones: ${this.responsavelTelefonesFormatados}`,
                        `E-mail: ${this.dadosContrato.responsavel_email}`,
                        `Endereço: ${this.dadosContrato.responsavel_endereco}`,
                        `Bairro: ${this.dadosContrato.responsavel_bairro}`,
                        `Cidade/UF: ${this.dadosContrato.responsavel_cidade}/${this.dadosContrato.responsavel_uf}`,
                        `CEP: ${this.dadosContrato.responsavel_cep}`
                      ]
                    },
                    {
                      stack: [
                        { text: 'ALUNO', bold: true, fontSize: 9 },
                        `Nome: ${this.dadosContrato.aluno_nome}`,
                        `Data Nasc.: ${this.dadosContrato.aluno_data_nascimento}`,
                        `Idade: ${this.idadeAluno}`,
                        { text: 'PLANO CONTRATADO', bold: true, margin: [0, 6, 0, 0] },
                        `Tipo de Plano: ${this.tipoPlanoContrato || ''}`,
                        `Aulas por Semana: ${this.aulasPorSemanaContrato || ''}`,
                        `Nível: ${this.nivelContrato || ''}`
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 20],
              fontSize: 9
            },
            // aqui o corpo processado com fonte e margin já ajustados
            ...pdfContent
          ],

          styles: {
            defaultStyle: { fontSize: 9, lineHeight: 1.2 }
          }
        };

        // 5) Gera o PDF do contrato como Blob (para permitir merge)
        const contractBlob = await this._pdfMakeToBlob(docDefinition);

        // 6) Se existir anexo, mescla no final
        const hasAnexo = !!(this.contratoAnexoCaminho && String(this.contratoAnexoCaminho).trim());
        const filename = `contrato_${this.dadosContrato?.id || this.dadosContrato?.matricula || 'matricula'}.pdf`;

        if (hasAnexo) {
          const anexoUrlAbs = this._absUploadUrl(this.contratoAnexoCaminho);
          const mergedBlob = await this._mergePdfWithAnexo(contractBlob, anexoUrlAbs);
          this._downloadBlob(mergedBlob, filename);
        } else {
          // sem anexo: baixa normal
          this._downloadBlob(contractBlob, filename);
        }
      }
      catch (error) {
        console.error('Erro pdfMake:', error);
      }
      finally {
        this.isGeneratingPdf = false;
      }
    },


      async _getLogoDataUrl() {
        const raw = this.$globalState?.logoPath || '';
        if (!raw) return null;

        const abs = (() => {
          try { return new URL(raw, window.location.origin).href; }
          catch { return raw; }
        })();

        // tenta com a URL completa
        try {
          const res = await fetch(abs, { cache: 'no-store' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          return await new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (_) {
          // fallback: sem querystring
          try {
            const u = new URL(abs);
            u.search = '';
            const res2 = await fetch(u.href, { cache: 'no-store' });
            if (!res2.ok) return null;
            const blob2 = await res2.blob();
            return await new Promise(resolve => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(blob2);
            });
          } catch {
            return null; // sem logo, mas seguimos com o PDF
          }
        }
      },
      fecharModal() { this.$emit('fechar'); }
    },
    async mounted() {
      await this.carregarEmpresa(); 
      await this.carregarContratoTemplate(); 
      this.isLoading = false;
    }
  };

</script>
  
  <style scoped>

@import "~quill/dist/quill.snow.css";
  
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-container {
    background: white;
    width: 100%;
    max-width: 70%;
    max-height: 90vh;
    overflow-y: auto;
    border-radius: 4px;
    padding: 20px;
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .close-icon {
    font-size: 1.5rem;
    cursor: pointer;
  }
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
  .header-logo {
    max-height: 60px;
  }
  .escola-info {
    text-align: center;
    font-size: 12px;
  }
  .recibo-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #ddd;
    padding-bottom: 10px;
    margin-bottom: 20px;
  }
  .escola-info p {
    line-height: 1.2;
    margin: 0;
    padding: 0;
  }
  .escola-info h4 {
    margin-bottom: 6px;
  }
  .contrato-corpo {
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.5;
  }
  .contrato-rodape {
    border-top: 1px solid #ddd;
    padding-top: 10px;
    text-align: center;
  }
  .data-local {
    font-size: 14px;
    margin-bottom: 15px;
  }
  .assinaturas {
    display: flex;
    justify-content: space-around;
    margin-top: 20px;
  }
  .assinatura {
    width: 45%;
    text-align: center;
  }
  .assinatura hr {
    border: none;
    border-top: 1px solid black;
    margin: 10px auto;
    width: 80%;
  }
  .btn-pdf {
  width: 70px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px; /* 🔹 Reduz o tamanho do texto */
  padding: 4px 8px; /* 🔹 Ajusta o preenchimento para reduzir o tamanho do botão */
  height: 32px; /* 🔹 Define uma altura menor */
}

  </style>
